import type { ProductContext } from "../types";

export interface AssistantProductCard {
  documentId: number;
  name: string;
  imageUrl: string | null;
  url: string | null;
  similarity: number;
  categories?: string[];
}

export interface AssistantQuoteItem {
  documentId: number;
  name: string;
  imageUrl: string | null;
  url: string | null;
  quantity: number;
}

export type AssistantProductPrice =
  | { documentId: number; status: "available"; price: string; originalPrice?: string }
  | { documentId: number; status: "range"; minPrice: string; maxPrice: string }
  | { documentId: number; status: "unavailable" };

export interface AssistantAction {
  type: "add" | "remove";
  productDocumentId: number;
}

export interface AssistantImageContext {
  caption: string;
  documentIds: number[];
}

export interface AssistantMessageBody {
  message: string;
  visitorId: string;
  pageUrl: string;
  product?: { sku?: string; gtin?: string; slug?: string; slugPath?: string };
  sessionId?: number;
  clientSessionId?: string;
  imageCaption?: string;
  imageProductDocumentIds?: number[];
  action?: AssistantAction;
}

export interface AssistantReply {
  sessionId: number;
  expiresAt: string;
  answer: string;
  products: AssistantProductCard[];
  categories?: string[];
  hasMoreProducts?: boolean;
  quoteItems: AssistantQuoteItem[];
}

export interface AssistantRestoredMessage {
  role: "user" | "assistant";
  content: string;
  createdAt?: string;
  imageCaption?: string;
  products?: AssistantProductCard[];
  categories?: string[];
  hasMoreProducts?: boolean;
}

export interface AssistantRestoredSession {
  sessionId: number;
  expiresAt: string;
  messages: AssistantRestoredMessage[];
  quoteItems: AssistantQuoteItem[];
}

export type AssistantFeedbackType = "bug" | "suggestion";

export type AssistantFailure = "disabled" | "quota" | "error";

export type AssistantResult =
  | { ok: true; reply: AssistantReply }
  | { ok: false; reason: AssistantFailure };

const MESSAGES_PATH = "/v1/metatag/assistant/messages";
const SESSION_PATH = "/v1/metatag/assistant/session";
const FEEDBACK_PATH = "/v1/metatag/assistant/feedback";
const PRICES_PATH = "/v1/metatag/assistant/prices";

function parsePositivePrice(value: unknown): string | null {
  if (typeof value !== "string" && typeof value !== "number") return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const amount = typeof value === "number" ? value : Number(value.replace(",", "."));
  return Number.isFinite(amount) && amount > 0 ? amount.toFixed(2) : null;
}
// Busca por imagem: no painel é multipart numa rota autenticada por conta. A rota pública ainda não
// existe no backend (item aberto). Mesmo formato do painel: campo "image" e resposta { caption, items }.
export const IMAGE_SEARCH_PATH = "/v1/metatag/assistant/image-search";

// Só manda o que tem valor: null/"" vira ausência de campo.
export function buildProductField(product: ProductContext | null): AssistantMessageBody["product"] {
  if (!product) return undefined;

  const fields: NonNullable<AssistantMessageBody["product"]> = {};
  if (product.sku) fields.sku = product.sku;
  if (product.gtin) fields.gtin = product.gtin;
  if (product.slug) fields.slug = product.slug;
  if (product.slugPath) fields.slugPath = product.slugPath;

  return Object.keys(fields).length > 0 ? fields : undefined;
}

export function buildAssistantBody(input: {
  message: string;
  visitorId: string;
  pageUrl: string;
  product: ProductContext | null;
  sessionId?: number;
  clientSessionId?: string;
  image?: AssistantImageContext;
  action?: AssistantAction;
}): AssistantMessageBody {
  const body: AssistantMessageBody = {
    message: input.message,
    visitorId: input.visitorId,
    pageUrl: input.pageUrl,
  };

  const product = buildProductField(input.product);
  if (product) body.product = product;
  if (input.sessionId) body.sessionId = input.sessionId;
  if (input.clientSessionId) body.clientSessionId = input.clientSessionId;
  if (input.image) {
    body.imageCaption = input.image.caption;
    body.imageProductDocumentIds = input.image.documentIds;
  }
  if (input.action) body.action = input.action;

  return body;
}

// Lista do servidor: descarta itens que não são objeto, para um item ruim não derrubar a renderização.
function asObjects<T>(value: unknown): T[] {
  return Array.isArray(value)
    ? (value as T[]).filter((item) => typeof item === "object" && item !== null)
    : [];
}

export async function sendAssistantMessage(
  apiBase: string,
  publicKey: string,
  body: AssistantMessageBody,
): Promise<AssistantResult> {
  try {
    const res = await fetch(`${apiBase}${MESSAGES_PATH}`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-indexaai-key": publicKey },
      body: JSON.stringify(body),
    });

    if (res.status === 403) return { ok: false, reason: "disabled" };
    if (res.status === 429) return { ok: false, reason: "quota" };
    if (!res.ok) return { ok: false, reason: "error" };

    const data = (await res.json()) as Partial<Record<keyof AssistantReply, unknown>> | null;
    if (!data || typeof data.answer !== "string" || typeof data.sessionId !== "number") {
      return { ok: false, reason: "error" };
    }

    return {
      ok: true,
      reply: {
        sessionId: data.sessionId,
        expiresAt: typeof data.expiresAt === "string" ? data.expiresAt : "",
        answer: data.answer,
        products: asObjects<AssistantProductCard>(data.products),
        ...(Array.isArray(data.categories)
          ? {
              categories: data.categories
                .filter((category): category is string => typeof category === "string")
                .slice(0, 5),
            }
          : {}),
        ...(data.hasMoreProducts === true ? { hasMoreProducts: true } : {}),
        quoteItems: asObjects<AssistantQuoteItem>(data.quoteItems),
      },
    };
  } catch {
    return { ok: false, reason: "error" };
  }
}

export async function fetchAssistantPrices(
  apiBase: string,
  publicKey: string,
  documentIds: number[],
): Promise<AssistantProductPrice[]> {
  const ids = [...new Set(documentIds.filter((id) => Number.isInteger(id) && id > 0))].slice(0, 12);
  if (ids.length === 0) return [];

  try {
    const res = await fetch(`${apiBase}${PRICES_PATH}`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-indexaai-key": publicKey },
      body: JSON.stringify({ documentIds: ids }),
    });
    if (!res.ok) return ids.map((documentId) => ({ documentId, status: "unavailable" }));

    const data = (await res.json()) as { items?: unknown } | null;
    const prices = Array.isArray(data?.items) ? data.items : [];
    const byId = new Map<number, AssistantProductPrice>();
    for (const value of prices) {
      if (typeof value !== "object" || value === null) continue;
      const item = value as Record<string, unknown>;
      if (
        typeof item.documentId !== "number" ||
        !Number.isInteger(item.documentId) ||
        !ids.includes(item.documentId)
      )
        continue;
      const documentId = Number(item.documentId);
      if (item.status === "available") {
        const price = parsePositivePrice(item.price);
        if (price === null) {
          byId.set(documentId, { documentId, status: "unavailable" });
          continue;
        }
        const originalPrice = parsePositivePrice(item.originalPrice);
        byId.set(documentId, {
          documentId,
          status: "available",
          price,
          ...(originalPrice !== null && Number(originalPrice) > Number(price)
            ? { originalPrice }
            : {}),
        });
      } else if (
        item.status === "range" &&
        typeof item.minPrice === "string" &&
        typeof item.maxPrice === "string"
      ) {
        const minPrice = parsePositivePrice(item.minPrice);
        const maxPrice = parsePositivePrice(item.maxPrice);
        byId.set(
          documentId,
          minPrice !== null && maxPrice !== null && Number(minPrice) <= Number(maxPrice)
            ? { documentId, status: "range", minPrice, maxPrice }
            : { documentId, status: "unavailable" },
        );
      } else if (item.status === "unavailable") {
        byId.set(documentId, { documentId, status: "unavailable" });
      }
    }

    return ids.map((documentId) => byId.get(documentId) ?? { documentId, status: "unavailable" });
  } catch {
    return ids.map((documentId) => ({ documentId, status: "unavailable" }));
  }
}

/** Restaura uma sessão validada pelo par sessionId/visitorId. */
export async function loadAssistantSession(
  apiBase: string,
  publicKey: string,
  input: { sessionId: number; visitorId: string },
): Promise<
  { ok: true; session: AssistantRestoredSession } | { ok: false; reason: "expired" | "error" }
> {
  try {
    const query = new URLSearchParams({
      sessionId: String(input.sessionId),
      visitorId: input.visitorId,
    });
    const res = await fetch(`${apiBase}${SESSION_PATH}?${query}`, {
      headers: { "x-indexaai-key": publicKey },
    });

    if (res.status === 404) return { ok: false, reason: "expired" };
    if (!res.ok) return { ok: false, reason: "error" };

    const data = (await res.json()) as Partial<
      Record<keyof AssistantRestoredSession, unknown>
    > | null;
    if (
      !data ||
      typeof data.sessionId !== "number" ||
      typeof data.expiresAt !== "string" ||
      !Array.isArray(data.messages)
    ) {
      return { ok: false, reason: "error" };
    }

    const messages = data.messages
      .filter(
        (message): message is AssistantRestoredMessage =>
          typeof message === "object" &&
          message !== null &&
          ((message as AssistantRestoredMessage).role === "user" ||
            (message as AssistantRestoredMessage).role === "assistant") &&
          typeof (message as AssistantRestoredMessage).content === "string",
      )
      .map((message) => ({
        ...message,
        ...(Array.isArray(message.products)
          ? { products: asObjects<AssistantProductCard>(message.products) }
          : {}),
        ...(Array.isArray(message.categories)
          ? {
              categories: message.categories
                .filter((category): category is string => typeof category === "string")
                .slice(0, 5),
            }
          : {}),
        ...(message.hasMoreProducts === true ? { hasMoreProducts: true } : {}),
      }));

    return {
      ok: true,
      session: {
        sessionId: data.sessionId,
        expiresAt: data.expiresAt,
        messages,
        quoteItems: asObjects<AssistantQuoteItem>(data.quoteItems),
      },
    };
  } catch {
    return { ok: false, reason: "error" };
  }
}

export async function sendAssistantFeedback(
  apiBase: string,
  publicKey: string,
  body: { visitorId: string; sessionId?: number; type: AssistantFeedbackType; message: string },
): Promise<{ ok: true } | { ok: false; reason: "rate-limited" | "error" }> {
  try {
    const res = await fetch(`${apiBase}${FEEDBACK_PATH}`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-indexaai-key": publicKey },
      body: JSON.stringify(body),
    });
    if (res.status === 429) return { ok: false, reason: "rate-limited" };
    if (!res.ok) return { ok: false, reason: "error" };
    return { ok: true };
  } catch {
    return { ok: false, reason: "error" };
  }
}

// Envia a foto como multipart (mesmo formato do painel). Devolve null em qualquer falha.
export async function searchByImage(
  apiBase: string,
  publicKey: string,
  image: File,
): Promise<AssistantImageContext | null> {
  const form = new FormData();
  form.append("image", image);

  try {
    const res = await fetch(`${apiBase}${IMAGE_SEARCH_PATH}`, {
      method: "POST",
      headers: { "x-indexaai-key": publicKey },
      body: form,
    });
    if (!res.ok) return null;

    const data = (await res.json()) as { caption?: unknown; items?: unknown } | null;
    const items = asObjects<{ documentId?: unknown }>(data?.items);
    return {
      caption: typeof data?.caption === "string" ? data.caption : "",
      documentIds: items
        .map((item) => item.documentId)
        .filter((id): id is number => typeof id === "number"),
    };
  } catch {
    return null;
  }
}
